<template>
  <div v-if="ticketCart.count > 0" class="cart-bar" :class="{ docked }">
    <div v-if="expanded" class="drawer pa-3">
      <div v-for="group in ticketCart.groups" :key="group.section.id" class="mb-2">
        <div class="cart-group-heading text-caption font-weight-bold text-uppercase mb-1">
          {{ groupName(group) }}
        </div>
        <div v-for="line in group.lines" :key="line.key" class="cart-row d-flex align-center pa-2 mb-1">
          <div class="flex-grow-1">
            <div class="font-weight-medium">
              {{ line.seat ? seatLabel(line.seat) : line.type.name }}
            </div>
            <div class="text-caption">
              <template v-if="line.seat">
                {{ line.type.name }} ·
              </template>
              {{ formatCurrency(priceOf(line)) }} + {{ formatCurrency(fee(priceOf(line))) }} de taxa
            </div>
          </div>
          <t-btn
            icon="mdi-trash-can-outline"
            variant="text"
            color="error"
            aria-label="Remover"
            @click="emit('remove', line)"
          />
        </div>
      </div>
    </div>
    <div class="bar d-flex align-center ga-3 px-4 py-3">
      <t-btn
        :icon="expanded ? 'mdi-chevron-down' : 'mdi-chevron-up'"
        variant="text"
        aria-label="Ver carrinho"
        @click="expanded = !expanded"
      />
      <div class="flex-grow-1">
        <div class="count text-uppercase font-weight-bold">
          {{ ticketCart.count }} {{ ticketCart.count === 1 ? "ingresso" : "ingressos" }} no carrinho
        </div>
        <div>
          {{ formatCurrency(ticketCart.subtotal) }}
          <small>+ {{ formatCurrency(ticketCart.fees) }} de taxas</small>
        </div>
      </div>
      <span v-if="holdLeft !== null && !mobile" class="hold-left d-flex align-center ga-1 text-caption">
        <v-icon size="16">mdi-timer-outline</v-icon>
        {{ holdLeft }}
      </span>
      <!-- On a phone the countdown sits under Avançar, so the summary keeps its width -->
      <div class="d-flex flex-column align-center">
        <t-btn
          color="success"
          :loading="busy"
          :disabled="busy"
          @click="emit('advance')"
        >
          Avançar
        </t-btn>
        <span v-if="holdLeft !== null && mobile" class="hold-left d-flex align-center ga-1 text-caption mt-1">
          <v-icon size="14">mdi-timer-outline</v-icon>
          {{ holdLeft }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, type Ref } from "vue";
import { useDisplay } from "vuetify";
import formatCurrency from "@/filters/currency";
import type Event from "@/types/Event";
import TBtn from "@/components/core/TBtn.vue";
import { useTicketCartStore } from "@/stores/ticketCart";
import Fee from "@/utils/fees";
import { seatLabel } from "@/utils/seatGrid";
import type { SectorGroup } from "@/utils/sectionSelection";
import type { CartGroup, CartLine } from "@/utils/ticketCart";

interface Props {
  event: Event;
  /** The listing's groups, which name a Sector's Sections when it is split. */
  groups: SectorGroup[];
  /** "14:59", the time left on the seat hold, null while no seat is held. */
  holdLeft: string | null;
  /** Pinned to the bottom of a full-screen view instead of the page. */
  docked?: boolean;
  /** Avançar is on its way: the page is reserving the Cart. */
  busy?: boolean;
}

const { event, groups, holdLeft, docked = false, busy = false } = defineProps<Props>();

const emit = defineEmits<{
  advance: [];
  remove: [line: CartLine];
}>();

defineOptions({
  name: "CartBar",
});

const ticketCart = useTicketCartStore();
const { mobile } = useDisplay();

/** The bar lists every ticket in the Cart. */
const expanded: Ref<boolean> = ref(false);

function fee(price: number): number {
  return Fee.calculate(price, event.convenience_fee, event.convenience_fee_mode);
}

function priceOf(line: CartLine): number {
  return line.type.current_batch?.price ?? 0;
}

/** Named as the listing names it: the Sector, followed by the Section when the Sector is split. */
function groupName(group: CartGroup): string {
  const split = groups.find(g => g.sector.id === group.sector.id)?.split ?? false;

  return split ? `${group.sector.name} · ${group.section.name}` : group.sector.name;
}
</script>

<style scoped>
.cart-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 201;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.15);
}

.cart-bar.docked {
  position: sticky;
}

.bar {
  background: rgb(var(--v-theme-primary));
  color: white;
}

.drawer {
  background: #fafafa;
  max-height: 50dvh;
  overflow-y: auto;
}

.cart-row {
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
}

.count {
  font-size: 0.8rem;
}
</style>
